import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteCampaignItem } from 'api/campaigns';
import { capitalize } from 'lodash';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignItem } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface DeleteItemAlertProps extends AlertDialog.AlertDialogRootProps {
    item: CampaignItem;
}

const DeleteItemAlert = (props: DeleteItemAlertProps) => {
    const toast = useToast();
    const { item, children, ...rest } = props;
    const { campaign_id } = useParams<{ campaign_id: string }>();
    const campaignId = parseInt(campaign_id);
    const queryClient = useQueryClient();
    const isAssociatedWithBundle = item.can_delete === false;

    const deleteItemMutation = useMutation({
        mutationFn: () => deleteCampaignItem({ campaignId, itemId: item.id }),
        onSuccess: () => {
            toast.success(`${capitalize(displayType(item.type))} deleted.`);
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.items(campaignId),
                type: 'all',
            });
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(
                typeof error.response?.data === 'string'
                    ? error.response.data
                    : `Unable to delete ${capitalize(displayType(item.type))}.`,
            );
        },
    });

    const description = isAssociatedWithBundle
        ? 'Are you sure you want to delete this ticket? Associated bundles will also be removed if the bundle contains only this ticket.'
        : 'This cannot be undone. Are you sure?';

    return (
        <AlertDialog.Root type={'error'} {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <div className="flex flex-col gap-8">
                            <div className="flex flex-col gap-1">
                                <AlertDialog.Title>
                                    Delete <em>{item.name}</em>?
                                </AlertDialog.Title>
                                <AlertDialog.Description>{description}</AlertDialog.Description>
                            </div>

                            <div className={'flex items-center justify-between gap-x-2'}>
                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                <AlertDialog.Action onClick={() => deleteItemMutation.mutate()}>
                                    Delete
                                </AlertDialog.Action>
                            </div>
                        </div>
                    </AlertDialog.Body>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const displayType = (type: string) => (type === 'ticket' ? 'ticket' : 'item');

const DeleteItemAlertTrigger = AlertDialog.Trigger;

const Root = DeleteItemAlert;
const Trigger = DeleteItemAlertTrigger;

export {
    DeleteItemAlert,
    DeleteItemAlertTrigger,
    //
    Root,
    Trigger,
};
